import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import {
    getFp1DeviceCustodyHistory,
    getFp1Devices,
    getReturnHistory,
    getReturnHistoryToOrg,
    updateFp1Device,
    Fp1ReturnedToOrgRow,
} from '../fp1.api';
import { Fp1CustodyEvent, Fp1Device } from '../fp1.types';
import {
    getAthleteChangeRequests,
    getSubscriptionItemChangeRequests,
    updateAthleteChangeRequest,
    updateSubscriptionItemChangeRequest,
} from '../../subscriptions/subscriptions.api';
import { AthleteChangeRequest, SubscriptionItemChangeRequest } from '../../../../../app.types';
import {
    getOrders,
    getOrderPodAssignments,
    getOrderReturnTrackingEvidence,
    updateOrder,
} from '../../orders/orders.api';
import { Order } from '../../orders/orders.types';
import { adminGetAllOrganizations } from '../../../admin.organizations.api';
import { Organization } from 'utils';
import {
    changePodOrganization,
    clearPodBilling,
    markPodReturned,
    markPodShipped,
} from '../../pods/pods.api';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatTs = (ts: number | string | null | undefined): string => {
    if (!ts) return '—';
    const d = typeof ts === 'number' ? new Date(ts * 1000) : new Date(ts);
    return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

const copyToClipboard = (text: string) => {
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).catch(console.error);
    } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
    }
};

// ─── Process Return Dialog ───────────────────────────────────────────────────
// Supports single pod or bulk (multiple pods from org search).

interface ProcessReturnDialogProps {
    open: boolean;
    /** One or more pods to process as a return. */
    pods: Fp1Device[];
    onClose: () => void;
    /** Called after ALL pods have been successfully returned. */
    onReturned: () => void;
    /** If set, this change request will be marked completed on success. */
    changeRequestUuid?: string;
    changeRequestType?: 'athlete' | 'org';
    /** Existing notes on the change request, preserved when auto-completing. */
    changeRequestNotes?: string | null;
}

const ProcessReturnDialog: React.FC<ProcessReturnDialogProps> = ({
    open,
    pods,
    onClose,
    onReturned,
    changeRequestUuid,
    changeRequestType,
    changeRequestNotes,
}) => {
    // visitedLinks tracks visited Stripe subscription IDs + pod UUIDs (for no-sub pods)
    const [visitedLinks, setVisitedLinks] = useState<Set<string>>(new Set());
    const [selectedReturnOrg, setSelectedReturnOrg] = useState<Organization | null>(null);
    const [orgOptions, setOrgOptions] = useState<Organization[]>([]);
    const [orgSearchLoading, setOrgSearchLoading] = useState(false);
    const orgSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    const searchOrgs = useCallback((term: string) => {
        if (orgSearchTimer.current) clearTimeout(orgSearchTimer.current);
        orgSearchTimer.current = setTimeout(async () => {
            setOrgSearchLoading(true);
            try {
                const res = await adminGetAllOrganizations(term, 50);
                const filtered = (res.data.data ?? []).filter(
                    (o) => !o.name.includes("'s Personal Organization"),
                );
                setOrgOptions(filtered);
            } catch {
                setOrgOptions([]);
            } finally {
                setOrgSearchLoading(false);
            }
        }, 300);
    }, []);

    useEffect(() => {
        if (open) {
            searchOrgs('');
        }
        return () => {
            if (orgSearchTimer.current) clearTimeout(orgSearchTimer.current);
        };
    }, [open, searchOrgs]);

    const handleClose = () => {
        setVisitedLinks(new Set());
        setSelectedReturnOrg(null);
        setConfirming(false);
        setError(null);
        setSuccess(false);
        onClose();
    };

    // Deduplicate Stripe subscriptions — multiple pods sharing one org sub only need one click.
    const uniqueSubIds = Array.from(
        new Set(
            pods
                .map((p) => p.stripe_linked_subscription?.subscription_id)
                .filter((id): id is string => !!id),
        ),
    );
    const noSubPods = pods.filter((p) => !p.stripe_linked_subscription?.subscription_id);
    const allLinksVisited =
        uniqueSubIds.every((id) => visitedLinks.has(id)) &&
        noSubPods.every((p) => visitedLinks.has(p.uuid));

    const handleStripeClick = (subId: string) => {
        window.open(
            `https://dashboard.stripe.com/subscriptions/${subId}`,
            '_blank',
            'noopener,noreferrer',
        );
        setVisitedLinks((prev) => new Set([...prev, subId]));
    };

    const handleConfirmReturn = async () => {
        setConfirming(true);
        setError(null);
        try {
            if (selectedReturnOrg) {
                // Reassigning to a different org — set deployed + new org in one call.
                // in_stock clears org automatically on the backend, so we must NOT
                // transition through in_stock when an org reassignment is intended.
                await Promise.all(
                    pods.map(async (pod) => {
                        await updateFp1Device(pod.uuid, {
                            status: 'deployed',
                            organization_uuid: selectedReturnOrg.uuid,
                            athlete_uuid: null,
                            event_notes: `Returned and reassigned to ${selectedReturnOrg.name}.`,
                        } as any);
                        // Wipe the previous org's billing arrangement so it doesn't follow
                        // the pod to the new org. Custody stays deployed (not In Stock).
                        await clearPodBilling(pod.uuid, {
                            notes: `Billing cleared on reassignment to ${selectedReturnOrg.name}.`,
                        });
                    }),
                );
            } else {
                // True return to warehouse — no org, in_stock
                await Promise.all(
                    pods.map((pod) =>
                        updateFp1Device(pod.uuid, {
                            status: 'in_stock',
                            athlete_uuid: null,
                            event_notes: 'Return processed via admin returns tab.',
                        } as any),
                    ),
                );
            }
            if (changeRequestUuid) {
                if (changeRequestType === 'athlete') {
                    await updateAthleteChangeRequest(changeRequestUuid, {
                        status: 'completed',
                        notes: changeRequestNotes ?? '',
                    });
                } else {
                    await updateSubscriptionItemChangeRequest(changeRequestUuid, {
                        status: 'completed',
                        notes: changeRequestNotes ?? '',
                    });
                }
            }
            setSuccess(true);
            onReturned();
        } catch (e: unknown) {
            const err = e as {
                response?: { data?: { message?: string; errors?: Record<string, string[]> } };
            };
            const flat = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat()[0]
                : undefined;
            setError(flat ?? err.response?.data?.message ?? 'Failed to process return.');
        } finally {
            setConfirming(false);
        }
    };

    const podCount = pods.length;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentReturnIcon color="error" />
                Process Return
                {podCount === 1 ? ` — ${pods[0]?.serial_number}` : ` — ${podCount} pods`}
            </DialogTitle>
            <DialogContent dividers>
                {success ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 3,
                            gap: 2,
                        }}
                    >
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
                        <Typography variant="h6" color="success.main">
                            Return Confirmed
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {selectedReturnOrg ? (
                                podCount === 1 ? (
                                    <>
                                        Pod <strong>{pods[0]?.serial_number}</strong> has been
                                        reassigned to <strong>{selectedReturnOrg.name}</strong> and
                                        set to Deployed.
                                    </>
                                ) : (
                                    <>
                                        <strong>{podCount} pods</strong> have been reassigned to{' '}
                                        <strong>{selectedReturnOrg.name}</strong> and set to
                                        Deployed.
                                    </>
                                )
                            ) : podCount === 1 ? (
                                <>
                                    Pod <strong>{pods[0]?.serial_number}</strong> has been
                                    dissociated and its status set to In Stock.
                                </>
                            ) : (
                                <>
                                    <strong>{podCount} pods</strong> have been dissociated and set
                                    to In Stock.
                                </>
                            )}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Pod list */}
                        {pods.map((pod) => {
                            const orgName = pod.assigned_org?.name ?? pod.organization_name ?? null;
                            const athleteName =
                                pod.assigned_athlete?.name ?? pod.athlete_name ?? null;
                            return (
                                <Paper
                                    key={pod.uuid}
                                    variant="outlined"
                                    sx={{ p: 2, borderRadius: 2 }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontWeight: 700,
                                                    fontSize: '14px',
                                                }}
                                            >
                                                {pod.serial_number}
                                            </Typography>
                                            {orgName && (
                                                <Typography
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ mt: 0.25 }}
                                                >
                                                    Org: <strong>{orgName}</strong>
                                                </Typography>
                                            )}
                                            {athleteName && (
                                                <Typography variant="body2" color="text.secondary">
                                                    Athlete: <strong>{athleteName}</strong>
                                                </Typography>
                                            )}
                                        </Box>
                                        {orgName && (
                                            <Tooltip title={`Search Stripe for "${orgName}"`}>
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    endIcon={<OpenInNewIcon fontSize="inherit" />}
                                                    onClick={() =>
                                                        window.open(
                                                            `https://dashboard.stripe.com/search?query=${encodeURIComponent(
                                                                orgName,
                                                            )}`,
                                                            '_blank',
                                                            'noopener,noreferrer',
                                                        )
                                                    }
                                                    sx={{ fontSize: '11px', flexShrink: 0 }}
                                                >
                                                    Stripe
                                                </Button>
                                            </Tooltip>
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}

                        {/* Stripe links — deduplicated by subscription ID */}
                        {uniqueSubIds.length > 0 && (
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Stripe subscription{uniqueSubIds.length > 1 ? 's' : ''} to
                                    cancel
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {uniqueSubIds.map((subId) => {
                                        const visited = visitedLinks.has(subId);
                                        // Find pods sharing this sub to show context
                                        const linked = pods.filter(
                                            (p) =>
                                                p.stripe_linked_subscription?.subscription_id ===
                                                subId,
                                        );
                                        const subName =
                                            linked[0]?.stripe_linked_subscription
                                                ?.subscription_name ?? null;
                                        return (
                                            <Box
                                                key={subId}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Button
                                                    variant={visited ? 'text' : 'outlined'}
                                                    size="small"
                                                    color={visited ? 'success' : 'primary'}
                                                    endIcon={
                                                        visited ? (
                                                            <CheckCircleOutlineIcon />
                                                        ) : (
                                                            <OpenInNewIcon />
                                                        )
                                                    }
                                                    onClick={() => handleStripeClick(subId)}
                                                >
                                                    {visited ? 'Stripe visited' : 'Open Stripe'}
                                                </Button>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontFamily: 'monospace' }}
                                                >
                                                    {subName ? `${subName} · ` : ''}
                                                    {subId.slice(0, 20)}…
                                                </Typography>
                                                <Tooltip title="Copy subscription ID">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => copyToClipboard(subId)}
                                                    >
                                                        <ContentCopyIcon fontSize="inherit" />
                                                    </IconButton>
                                                </Tooltip>
                                                {linked.length > 1 && (
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                    >
                                                        (covers {linked.length} pods)
                                                    </Typography>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Paper>
                        )}

                        {/* No-sub pods — still need manual confirmation per pod */}
                        {noSubPods.map((pod) => (
                            <FormControlLabel
                                key={pod.uuid}
                                control={
                                    <Checkbox
                                        checked={visitedLinks.has(pod.uuid)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setVisitedLinks(
                                                    (prev) => new Set([...prev, pod.uuid]),
                                                );
                                            } else {
                                                setVisitedLinks((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(pod.uuid);
                                                    return next;
                                                });
                                            }
                                        }}
                                        size="small"
                                    />
                                }
                                label={
                                    <Typography variant="caption">
                                        {pod.serial_number} — No sub, confirm acknowledged
                                    </Typography>
                                }
                            />
                        ))}

                        {/* Reassign org (optional) */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Reassign to organization (optional)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Search for the org to reassign the pod to (e.g. AMT, Legacy Sports).
                                If selected, the pod will be set to <strong>Deployed</strong> under
                                that org. Leave blank to set the pod to <strong>In Stock</strong>{' '}
                                with no org.
                            </Typography>
                            <Autocomplete
                                size="small"
                                value={selectedReturnOrg}
                                onChange={(_, v) => setSelectedReturnOrg(v)}
                                onInputChange={(_, val, reason) => {
                                    if (reason !== 'reset') searchOrgs(val);
                                }}
                                options={orgOptions}
                                getOptionLabel={(o) => o.name}
                                filterOptions={(o) => o}
                                loading={orgSearchLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Organization"
                                        placeholder="Search by name…"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {orgSearchLoading && (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={16}
                                                        />
                                                    )}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Paper>

                        {!allLinksVisited && (
                            <Alert severity="warning">
                                Open Stripe for each pod with an active subscription before
                                confirming.
                            </Alert>
                        )}
                        {error && <Alert severity="error">{error}</Alert>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>{success ? 'Close' : 'Cancel'}</Button>
                {!success && (
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!allLinksVisited || confirming || pods.length === 0}
                        onClick={handleConfirmReturn}
                        startIcon={
                            confirming ? <CircularProgress size={16} color="inherit" /> : undefined
                        }
                    >
                        Confirm Return{podCount > 1 ? ` (${podCount})` : ''}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

// ─── Replacement Return Dialog ───────────────────────────────────────────────
// No Stripe requirement — just serial lookup + device status update + archive order.

interface ReplacementReturnDialogProps {
    open: boolean;
    order: Order | null;
    prefilledSerial: string | null;
    onClose: () => void;
    /** Called after the return is successfully processed. */
    onCompleted: () => void;
}

const ReplacementReturnDialog: React.FC<ReplacementReturnDialogProps> = ({
    open,
    order,
    prefilledSerial,
    onClose,
    onCompleted,
}) => {
    const [serialInput, setSerialInput] = useState('');
    const [deviceResults, setDeviceResults] = useState<Fp1Device[]>([]);
    const [device, setDevice] = useState<Fp1Device | null>(null);
    const [deviceLoading, setDeviceLoading] = useState(false);
    const [deviceError, setDeviceError] = useState<string | null>(null);
    const [selectedReturnOrg, setSelectedReturnOrg] = useState<Organization | null>(null);
    const [orgOptions, setOrgOptions] = useState<Organization[]>([]);
    const [orgSearchLoading, setOrgSearchLoading] = useState(false);
    const orgSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    /** Who the old pod was most recently associated with, before it was auto-detached at ship time. */
    const [lastAssociation, setLastAssociation] = useState<{
        orgName: string | null;
        athleteName: string | null;
    } | null>(null);

    const searchOrgs = useCallback((term: string) => {
        if (orgSearchTimer.current) clearTimeout(orgSearchTimer.current);
        orgSearchTimer.current = setTimeout(async () => {
            setOrgSearchLoading(true);
            try {
                const res = await adminGetAllOrganizations(term, 50);
                const filtered = (res.data.data ?? []).filter(
                    (o) => !o.name.includes("'s Personal Organization"),
                );
                setOrgOptions(filtered);
            } catch {
                setOrgOptions([]);
            } finally {
                setOrgSearchLoading(false);
            }
        }, 300);
    }, []);

    useEffect(() => {
        if (open) {
            setSerialInput(prefilledSerial ?? '');
            setDeviceResults([]);
            setDevice(null);
            setDeviceError(null);
            setError(null);
            setSuccess(false);
            setSelectedReturnOrg(null);
            setLastAssociation(null);
            searchOrgs('');
            if (order) {
                getOrderPodAssignments(order.uuid)
                    .then((res) => {
                        const oldPod = res.data?.data?.old_pod;
                        if (oldPod) {
                            setLastAssociation({
                                orgName: oldPod.org_name,
                                athleteName: oldPod.athlete_name,
                            });
                        }
                    })
                    .catch(() => {
                        /* non-fatal — just skip showing the historical association */
                    });
            }
        }
        return () => {
            if (orgSearchTimer.current) clearTimeout(orgSearchTimer.current);
        };
    }, [open, order, prefilledSerial, searchOrgs]);

    // Auto-lookup devices when serial is long enough — show results as clickable cards
    useEffect(() => {
        if (!open) return;
        const trimmed = serialInput.trim();
        if (trimmed.length < 4) {
            setDeviceResults([]);
            setDevice(null);
            setDeviceError(null);
            return;
        }
        const timer = setTimeout(async () => {
            setDeviceLoading(true);
            setDeviceError(null);
            setDevice(null);
            setDeviceResults([]);
            try {
                const res = await getFp1Devices({ serial_number: trimmed, per_page: 10, page: 1 });
                const results = res.data?.data ?? [];
                setDeviceResults(results);
                // Auto-select if exactly one result
                if (results.length === 1) {
                    setDevice(results[0]);
                } else if (results.length === 0) {
                    setDeviceError('No pod found with that serial number.');
                }
            } catch {
                setDeviceError('Failed to look up pod.');
            } finally {
                setDeviceLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [serialInput, open]);

    const handleClose = () => {
        if (confirming) return;
        onClose();
    };

    const handleConfirm = async () => {
        if (!device || !order) return;
        setConfirming(true);
        setError(null);
        try {
            // The old pod was already detached from its org/athlete and moved to custody
            // 'returning' when the replacement order shipped — this finalizes the physical
            // return by flipping it to 'returned' (or immediately redeploying it elsewhere).
            if (selectedReturnOrg) {
                await changePodOrganization(device.uuid, selectedReturnOrg.uuid);
                await markPodShipped(device.uuid, {
                    tracking_number:
                        order.shipping_tracking_number || order.return_tracking_number || 'N/A',
                    notes: `Replacement return processed. Reassigned to ${selectedReturnOrg.name}.`,
                });
                // Reassigning to a new org: wipe the previous org's billing arrangement
                // (comp/invoice/subscription/transfer) so it doesn't follow the pod. Custody
                // stays as the reassignment set it (shipped), not In Stock.
                await clearPodBilling(device.uuid, {
                    notes: `Billing cleared on reassignment to ${selectedReturnOrg.name}.`,
                });
            } else {
                await markPodReturned(device.uuid, {
                    notes: 'Replacement return processed via admin returns tab.',
                });
            }
            // Archive the order so it leaves the active returns table and lands in Return History
            await updateOrder(order.uuid, {
                order_status: 'archived',
                order_notes: order.order_notes,
                shipping_tracking_number: order.shipping_tracking_number ?? '',
                return_tracking_number: order.return_tracking_number ?? undefined,
            });
            setSuccess(true);
            onCompleted();
        } catch (e: unknown) {
            const err = e as {
                response?: { data?: { message?: string; errors?: Record<string, string[]> } };
            };
            const flat = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat()[0]
                : undefined;
            setError(flat ?? err.response?.data?.message ?? 'Failed to process return.');
        } finally {
            setConfirming(false);
        }
    };

    const orgName =
        order?.buyer_org_name ??
        (order?.buyer_athlete
            ? `${order.buyer_athlete.first_name} ${order.buyer_athlete.last_name}`
            : null);

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AssignmentReturnIcon color="error" />
                Complete Replacement Return
                {orgName ? ` — ${orgName}` : ''}
            </DialogTitle>
            <DialogContent dividers>
                {success ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            py: 3,
                            gap: 2,
                        }}
                    >
                        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 56 }} />
                        <Typography variant="h6" color="success.main">
                            Return Confirmed
                        </Typography>
                        <Typography variant="body2" color="text.secondary" textAlign="center">
                            {selectedReturnOrg ? (
                                <>
                                    Pod <strong>{device?.serial_number}</strong> has been reassigned
                                    to <strong>{selectedReturnOrg.name}</strong> and set to
                                    Deployed.
                                </>
                            ) : (
                                <>
                                    Pod <strong>{device?.serial_number}</strong> has been marked
                                    Returned and the replacement order has been archived.
                                </>
                            )}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Enter the serial number of the pod that was returned. The pod will be
                            marked <strong>Returned</strong> and the replacement order will be
                            archived (removed from the active returns table).
                        </Typography>

                        {/* Serial number input with live lookup */}
                        <TextField
                            label="Pod Serial Number"
                            size="small"
                            value={serialInput}
                            onChange={(e) => setSerialInput(e.target.value)}
                            placeholder="e.g. FP1-XXXX (min 4 chars to auto-search)"
                            InputProps={{
                                endAdornment: deviceLoading ? (
                                    <CircularProgress size={16} />
                                ) : undefined,
                                sx: { fontFamily: 'monospace' },
                            }}
                            error={!!deviceError}
                            helperText={deviceError ?? undefined}
                            fullWidth
                        />

                        {(lastAssociation?.orgName || lastAssociation?.athleteName) && (
                            <Alert severity="info" sx={{ fontSize: '12px' }}>
                                Most recently associated with:{' '}
                                <strong>
                                    {lastAssociation.orgName ?? '—'}
                                    {lastAssociation.athleteName
                                        ? ` · ${lastAssociation.athleteName}`
                                        : ''}
                                </strong>
                            </Alert>
                        )}

                        {/* Search results — clickable cards; auto-selected when only one match */}
                        {deviceResults.length > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {deviceResults.length > 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                        {deviceResults.length} pods found — click one to select it
                                    </Typography>
                                )}
                                {deviceResults.map((d) => {
                                    const isSelected = device?.uuid === d.uuid;
                                    const orgLabel =
                                        d.assigned_org?.name ?? d.organization_name ?? '—';
                                    return (
                                        <Paper
                                            key={d.uuid}
                                            variant="outlined"
                                            onClick={() => setDevice(d)}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                borderColor: isSelected
                                                    ? 'success.main'
                                                    : 'divider',
                                                boxShadow: isSelected
                                                    ? (t) => `0 0 0 2px ${t.palette.success.main}`
                                                    : undefined,
                                                transition: 'border-color 0.15s, box-shadow 0.15s',
                                                '&:hover': {
                                                    borderColor: 'success.main',
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: 'monospace',
                                                            fontWeight: 700,
                                                            fontSize: '14px',
                                                        }}
                                                    >
                                                        {d.serial_number}
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        Status: <strong>{d.status ?? '—'}</strong>{' '}
                                                        &nbsp;·&nbsp; Org:{' '}
                                                        <strong>{orgLabel}</strong>
                                                    </Typography>
                                                    {d.assigned_athlete && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Athlete:{' '}
                                                            <strong>
                                                                {d.assigned_athlete.name ??
                                                                    d.assigned_athlete.email}
                                                            </strong>
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {isSelected && (
                                                    <CheckCircleOutlineIcon color="success" />
                                                )}
                                            </Box>
                                        </Paper>
                                    );
                                })}
                            </Box>
                        )}

                        {/* Reassign org (optional) */}
                        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                Reassign to organization (optional)
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Leave blank to set the pod to <strong>In Stock</strong>. Select an
                                org to reassign it to <strong>Deployed</strong> under that org
                                instead.
                            </Typography>
                            <Autocomplete
                                size="small"
                                value={selectedReturnOrg}
                                onChange={(_, v) => setSelectedReturnOrg(v)}
                                onInputChange={(_, val, reason) => {
                                    if (reason !== 'reset') searchOrgs(val);
                                }}
                                options={orgOptions}
                                getOptionLabel={(o) => o.name}
                                filterOptions={(o) => o}
                                loading={orgSearchLoading}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Organization"
                                        placeholder="Search by name…"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {orgSearchLoading && (
                                                        <CircularProgress
                                                            color="inherit"
                                                            size={16}
                                                        />
                                                    )}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Paper>

                        {error && <Alert severity="error">{error}</Alert>}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={confirming}>
                    {success ? 'Close' : 'Cancel'}
                </Button>
                {!success && (
                    <Button
                        variant="contained"
                        color="error"
                        disabled={!device || deviceLoading || confirming}
                        onClick={handleConfirm}
                        startIcon={
                            confirming ? <CircularProgress size={16} color="inherit" /> : undefined
                        }
                    >
                        Confirm Return
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

// ─── Return History Dialog ───────────────────────────────────────────────────

interface CustodyInfo {
    processedBy: string | null;
    lastShippedAt: string | null;
}

const ReturnHistoryDialog: React.FC<{ open: boolean; onClose: () => void }> = ({
    open,
    onClose,
}) => {
    const [activeTab, setActiveTab] = useState(0);

    // ── Pod Returns tab ──────────────────────────────────────────────────────
    const [rows, setRows] = useState<Fp1Device[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
    const [serialSearch, setSerialSearch] = useState('');
    const [orgSearch, setOrgSearch] = useState('');
    const [athleteSearch, setAthleteSearch] = useState('');
    const [searchDraft, setSearchDraft] = useState({
        serial: '',
        org: '',
        athlete: '',
    });
    const [custodyMap, setCustodyMap] = useState<Record<string, CustodyInfo>>({});
    const [loadingCustody, setLoadingCustody] = useState(false);

    // ── Replacement Returns tab ──────────────────────────────────────────────
    const [replacementRows, setReplacementRows] = useState<Order[]>([]);
    const [replacementLoading, setReplacementLoading] = useState(false);
    const [replacementPodSerialMap, setReplacementPodSerialMap] = useState<
        Record<string, string | null>
    >({});

    // ── Returned to Org tab ──────────────────────────────────────────────────
    // Pods handed to a designated partner org rather than returned to Fiyrpod/AMT's own
    // warehouse. Backed by the structured `returned_to_org` custody event; read-only.
    const [orgReturnRows, setOrgReturnRows] = useState<Fp1ReturnedToOrgRow[]>([]);
    const [orgReturnLoading, setOrgReturnLoading] = useState(false);
    const [orgReturnTotalRows, setOrgReturnTotalRows] = useState(0);
    const [orgReturnPagination, setOrgReturnPagination] = useState({ page: 0, pageSize: 25 });
    const [orgSerialSearch, setOrgSerialSearch] = useState('');
    const [orgDestSearch, setOrgDestSearch] = useState('');
    const [orgAthleteSearch, setOrgAthleteSearch] = useState('');
    const [orgReturnSearchDraft, setOrgReturnSearchDraft] = useState({
        serial: '',
        org: '',
        athlete: '',
    });

    const fetchCompletedReplacements = useCallback(async () => {
        setReplacementLoading(true);
        setReplacementPodSerialMap({});
        try {
            const res = await getOrders({
                order_type: 'replacement',
                order_status: 'archived',
                per_page: 100,
                sort: '-created_at',
            });
            const withTracking = (res.data?.data ?? []).filter((o) => !!o.return_tracking_number);
            setReplacementRows(withTracking);
            if (withTracking.length > 0) {
                const results = await Promise.allSettled(
                    withTracking.map((o) => getOrderPodAssignments(o.uuid)),
                );
                const newMap: Record<string, string | null> = {};
                results.forEach((result, i) => {
                    const orderUuid = withTracking[i].uuid;
                    if (result.status === 'fulfilled') {
                        // The pod actually being returned is the OLD pod, not the new one shipped out.
                        newMap[orderUuid] = result.value.data?.data?.old_pod?.serial_number ?? null;
                    } else {
                        newMap[orderUuid] = null;
                    }
                });
                setReplacementPodSerialMap(newMap);
            }
        } catch (err) {
            console.error('Failed to fetch completed replacement orders', err);
        } finally {
            setReplacementLoading(false);
        }
    }, []);

    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;
    const searchRef = useRef(searchDraft);
    searchRef.current = searchDraft;

    const orgReturnPaginationRef = useRef(orgReturnPagination);
    orgReturnPaginationRef.current = orgReturnPagination;
    const orgReturnSearchRef = useRef(orgReturnSearchDraft);
    orgReturnSearchRef.current = orgReturnSearchDraft;

    const loadCustodyInfo = useCallback(async (uuids: string[]) => {
        if (uuids.length === 0) return;
        setLoadingCustody(true);
        try {
            const results = await Promise.allSettled(
                uuids.map((uuid) => getFp1DeviceCustodyHistory(uuid)),
            );
            const newMap: Record<string, CustodyInfo> = {};
            results.forEach((result, i) => {
                const uuid = uuids[i];
                if (result.status === 'fulfilled') {
                    const raw = result.value.data;
                    const events: Fp1CustodyEvent[] = Array.isArray(raw)
                        ? raw
                        : (raw as any).data ?? [];
                    // Most recent returned_to_warehouse event → who processed the return
                    const returnEvt = events.find((e) => e.event_type === 'returned_to_warehouse');
                    // Most recent deployed/shipped event → when pod was last sent out
                    const deployEvt = events.find(
                        (e) =>
                            e.event_type === 'deployed' ||
                            e.event_type === 'shipped_to_athlete' ||
                            e.to_status === 'deployed',
                    );
                    newMap[uuid] = {
                        processedBy: returnEvt?.performed_by_name ?? null,
                        lastShippedAt: deployEvt?.created_at ?? null,
                    };
                } else {
                    newMap[uuid] = { processedBy: null, lastShippedAt: null };
                }
            });
            setCustodyMap((prev) => ({ ...prev, ...newMap }));
        } finally {
            setLoadingCustody(false);
        }
    }, []);

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        setCustodyMap({});
        try {
            const res = await getReturnHistory({
                serial_number: searchRef.current.serial || undefined,
                org_name: searchRef.current.org || undefined,
                athlete_name: searchRef.current.athlete || undefined,
                page: paginationRef.current.page + 1,
                per_page: paginationRef.current.pageSize,
            });
            const newRows = res.data?.data ?? [];
            setRows(newRows);
            setTotalRows(res.data?.meta?.total ?? 0);
            void loadCustodyInfo(newRows.map((r) => r.uuid));
        } catch (err) {
            console.error('Failed to fetch return history', err);
        } finally {
            setLoading(false);
        }
    }, [loadCustodyInfo]);

    const fetchOrgReturns = useCallback(async () => {
        setOrgReturnLoading(true);
        try {
            const res = await getReturnHistoryToOrg({
                serial_number: orgReturnSearchRef.current.serial || undefined,
                org_name: orgReturnSearchRef.current.org || undefined,
                athlete_name: orgReturnSearchRef.current.athlete || undefined,
                page: orgReturnPaginationRef.current.page + 1,
                per_page: orgReturnPaginationRef.current.pageSize,
            });
            setOrgReturnRows(res.data?.data ?? []);
            setOrgReturnTotalRows(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch returned-to-org history', err);
        } finally {
            setOrgReturnLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            void fetchHistory();
            void fetchCompletedReplacements();
        }
    }, [open, fetchHistory, fetchCompletedReplacements, pagination.page, pagination.pageSize]);

    useEffect(() => {
        if (open) {
            void fetchOrgReturns();
        }
    }, [open, fetchOrgReturns, orgReturnPagination.page, orgReturnPagination.pageSize]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setSearchDraft({ serial: serialSearch, org: orgSearch, athlete: athleteSearch });
        setPagination((p) => ({ ...p, page: 0 }));
        setTimeout(() => void fetchHistory(), 0);
    };

    const handleOrgReturnSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setOrgReturnSearchDraft({
            serial: orgSerialSearch,
            org: orgDestSearch,
            athlete: orgAthleteSearch,
        });
        setOrgReturnPagination((p) => ({ ...p, page: 0 }));
        setTimeout(() => void fetchOrgReturns(), 0);
    };

    const columns: GridColDef[] = [
        {
            field: 'serial_number',
            headerName: 'Serial',
            width: 160,
            renderCell: (params) => (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'org_name',
            headerName: 'Organization',
            width: 200,
            sortable: false,
            renderCell: (params) =>
                params.row.assigned_org?.name ?? params.row.organization_name ?? '—',
        },
        {
            field: 'athlete_name',
            headerName: 'Athlete',
            width: 180,
            sortable: false,
            renderCell: (params) =>
                params.row.assigned_athlete?.name ?? params.row.athlete_name ?? '—',
        },
        {
            field: 'processed_by',
            headerName: 'Processed By',
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const info = custodyMap[params.row.uuid];
                if (info === undefined) {
                    return loadingCustody ? <CircularProgress size={14} /> : '—';
                }
                return info.processedBy ?? '—';
            },
        },
        {
            field: 'subscription_status',
            headerName: 'Sub Status',
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as Fp1Device;
                const subId = row.stripe_linked_subscription?.subscription_id;
                const hasAthlete = !!(row.assigned_athlete?.uuid ?? row.athlete_uuid);

                if (hasAthlete) {
                    // Athlete pod — show whether their subscription was cancelled
                    return (
                        <Chip
                            size="small"
                            label="Cancelled"
                            color="success"
                            sx={{ fontSize: '11px' }}
                        />
                    );
                }

                // Org pod — link to the Stripe subscription page
                if (subId) {
                    return (
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon fontSize="inherit" />}
                            onClick={() =>
                                window.open(
                                    `https://dashboard.stripe.com/subscriptions/${subId}`,
                                    '_blank',
                                    'noopener,noreferrer',
                                )
                            }
                            sx={{ fontSize: '11px', py: 0.25 }}
                        >
                            View Stripe
                        </Button>
                    );
                }

                return <span>—</span>;
            },
        },
        {
            field: 'last_returned_at',
            headerName: 'Returned At',
            width: 140,
            valueFormatter: (value: any) => formatTs(value),
        },
        {
            field: 'last_shipped_at',
            headerName: 'Last Shipped',
            width: 140,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as Fp1Device;
                const ts = row.last_shipped_at ?? custodyMap[row.uuid]?.lastShippedAt;
                return formatTs(ts);
            },
        },
    ];

    const replacementColumns: GridColDef[] = [
        {
            field: 'recipient',
            headerName: 'Recipient',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const o = params.row as Order;
                return (
                    o.buyer_org_name ??
                    (o.buyer_athlete
                        ? `${o.buyer_athlete.first_name} ${o.buyer_athlete.last_name}`
                        : '—')
                );
            },
        },
        {
            field: 'pod_serial',
            headerName: 'Pod Serial',
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const serial = replacementPodSerialMap[params.row.uuid];
                if (serial === undefined) return <CircularProgress size={14} />;
                if (!serial) return <span>—</span>;
                return (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {serial}
                    </Typography>
                );
            },
        },
        {
            field: 'return_tracking_number',
            headerName: 'Return Tracking',
            width: 190,
            sortable: false,
            renderCell: (params) => {
                const tracking = params.value as string | null | undefined;
                if (!tracking) return <span>—</span>;
                return (
                    <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewIcon fontSize="inherit" />}
                        onClick={() =>
                            window.open(
                                `https://www.ups.com/track?loc=en_US&tracknum=${tracking}`,
                                '_blank',
                                'noopener,noreferrer',
                            )
                        }
                        sx={{ fontSize: '12px', fontFamily: 'monospace', px: 0, minWidth: 0 }}
                    >
                        {tracking}
                    </Button>
                );
            },
        },
        {
            field: 'shipping_tracking_number',
            headerName: 'Outbound Tracking',
            width: 190,
            sortable: false,
            renderCell: (params) => {
                const tracking = params.value as string | null | undefined;
                if (!tracking) return <span>—</span>;
                return (
                    <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewIcon fontSize="inherit" />}
                        onClick={() =>
                            window.open(
                                `https://www.ups.com/track?loc=en_US&tracknum=${tracking}`,
                                '_blank',
                                'noopener,noreferrer',
                            )
                        }
                        sx={{ fontSize: '12px', fontFamily: 'monospace', px: 0, minWidth: 0 }}
                    >
                        {tracking}
                    </Button>
                );
            },
        },
        {
            field: 'created_at',
            headerName: 'Order Date',
            width: 130,
            valueFormatter: (value: any) => formatTs(value),
        },
    ];

    const orgReturnColumns: GridColDef[] = [
        {
            field: 'serial_number',
            headerName: 'Serial',
            width: 160,
            renderCell: (params) => (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
                    {params.value ?? '—'}
                </Typography>
            ),
        },
        {
            field: 'destination_org_name',
            headerName: 'Returned To (Org)',
            width: 220,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as Fp1ReturnedToOrgRow;
                return row.destination_org_name ?? '—';
            },
        },
        {
            field: 'previous_org_name',
            headerName: 'Previously Assigned To',
            width: 240,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as Fp1ReturnedToOrgRow;
                const athlete = row.previous_athlete_name;
                const org = row.previous_org_name;
                if (athlete && org) return `${athlete} · ${org}`;
                return athlete ?? org ?? '—';
            },
        },
        {
            field: 'processed_by',
            headerName: 'Processed By',
            width: 160,
            sortable: false,
            renderCell: (params) => (params.row as Fp1ReturnedToOrgRow).processed_by ?? '—',
        },
        {
            field: 'returned_at',
            headerName: 'Returned At',
            width: 160,
            renderCell: (params) => formatTs((params.row as Fp1ReturnedToOrgRow).returned_at),
        },
    ];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon color="primary" />
                Return History
            </DialogTitle>
            <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Pod Returns" />
                <Tab
                    label={
                        replacementRows.length > 0
                            ? `Replacement Returns (${replacementRows.length})`
                            : 'Replacement Returns'
                    }
                />
                <Tab label="Returned to Org" />
            </Tabs>
            <DialogContent dividers>
                {activeTab === 0 && (
                    <>
                        <Box
                            component="form"
                            onSubmit={handleSearch}
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                mb: 2,
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                            }}
                        >
                            <TextField
                                size="small"
                                label="Serial number"
                                value={serialSearch}
                                onChange={(e) => setSerialSearch(e.target.value)}
                                sx={{ width: 180 }}
                            />
                            <TextField
                                size="small"
                                label="Organization name"
                                value={orgSearch}
                                onChange={(e) => setOrgSearch(e.target.value)}
                                sx={{ width: 200 }}
                            />
                            <TextField
                                size="small"
                                label="Athlete name / email"
                                value={athleteSearch}
                                onChange={(e) => setAthleteSearch(e.target.value)}
                                sx={{ width: 200 }}
                            />
                            <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
                                Search
                            </Button>
                        </Box>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <DataGridPremium
                                rows={rows}
                                columns={columns}
                                getRowId={(row) => row.uuid}
                                loading={loading}
                                rowCount={totalRows}
                                pagination
                                paginationMode="server"
                                paginationModel={pagination}
                                onPaginationModelChange={setPagination}
                                pageSizeOptions={[10, 25, 50]}
                                disableColumnMenu
                                autoHeight
                                getRowHeight={() => 52}
                            />
                        </Paper>
                    </>
                )}
                {activeTab === 1 && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Replacement orders where the defective pod has been returned and
                            processed. These are archived once a return is completed via the
                            &ldquo;Complete Return&rdquo; button on the active returns table.
                        </Typography>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <DataGridPremium
                                rows={replacementRows}
                                columns={replacementColumns}
                                getRowId={(row) => row.uuid}
                                loading={replacementLoading}
                                disableColumnMenu
                                autoHeight
                                hideFooter={replacementRows.length <= 10}
                                getRowHeight={() => 52}
                            />
                        </Paper>
                    </>
                )}
                {activeTab === 2 && (
                    <>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Pods that were returned and handed to a designated partner organization
                            (rather than back to Fiyrpod/AMT&rsquo;s own warehouse). &ldquo;Returned
                            To&rdquo; is the receiving org; &ldquo;Previously Assigned To&rdquo; is
                            who held the pod before the return.
                        </Typography>
                        <Box
                            component="form"
                            onSubmit={handleOrgReturnSearch}
                            sx={{
                                display: 'flex',
                                gap: 1.5,
                                mb: 2,
                                flexWrap: 'wrap',
                                alignItems: 'flex-end',
                            }}
                        >
                            <TextField
                                size="small"
                                label="Serial number"
                                value={orgSerialSearch}
                                onChange={(e) => setOrgSerialSearch(e.target.value)}
                                sx={{ width: 180 }}
                            />
                            <TextField
                                size="small"
                                label="Receiving org name"
                                value={orgDestSearch}
                                onChange={(e) => setOrgDestSearch(e.target.value)}
                                sx={{ width: 200 }}
                            />
                            <TextField
                                size="small"
                                label="Previous athlete name / email"
                                value={orgAthleteSearch}
                                onChange={(e) => setOrgAthleteSearch(e.target.value)}
                                sx={{ width: 220 }}
                            />
                            <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
                                Search
                            </Button>
                        </Box>
                        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            <DataGridPremium
                                rows={orgReturnRows}
                                columns={orgReturnColumns}
                                getRowId={(row) => row.uuid}
                                loading={orgReturnLoading}
                                rowCount={orgReturnTotalRows}
                                pagination
                                paginationMode="server"
                                paginationModel={orgReturnPagination}
                                onPaginationModelChange={setOrgReturnPagination}
                                pageSizeOptions={[10, 25, 50]}
                                disableColumnMenu
                                autoHeight
                                getRowHeight={() => 52}
                            />
                        </Paper>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

// ─── Initiate Return Dialog ──────────────────────────────────────────────────

type SearchType = 'serial' | 'org' | 'athlete';

const InitiateReturnDialog: React.FC<{ open: boolean; onClose: () => void }> = ({
    open,
    onClose,
}) => {
    const [searchType, setSearchType] = useState<SearchType>('serial');
    const [searchValue, setSearchValue] = useState('');
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState<Fp1Device[]>([]);
    const [searched, setSearched] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [processReturnPods, setProcessReturnPods] = useState<Fp1Device[] | null>(null);
    /** Persists across searches — maps uuid → pod so we keep full device data */
    const [selectedPodsMap, setSelectedPodsMap] = useState<Record<string, Fp1Device>>({});
    const autoSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleClose = () => {
        setSearchValue('');
        setResults([]);
        setSearched(false);
        setError(null);
        setSelectedPodsMap({});
        onClose();
    };

    const runSearch = useCallback(async (value: string, type: SearchType) => {
        const term = value.trim();
        if (!term) return;
        setLoading(true);
        setError(null);
        setSearched(false);
        // Do NOT clear selectedPodsMap here — selections persist across searches
        try {
            const params: Record<string, any> = { per_page: 100, page: 1 };
            if (type === 'serial') {
                params.serial_number = term;
            } else if (type === 'athlete') {
                params.athlete_name = term;
            } else {
                params.org_name = term;
            }
            const res = await getFp1Devices(params);
            setResults(res.data?.data ?? []);
            setSearched(true);
        } catch (err) {
            setError('Search failed. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Auto-search when serial field reaches 4+ characters
    useEffect(() => {
        if (searchType !== 'serial') return;
        if (autoSearchTimer.current) clearTimeout(autoSearchTimer.current);
        if (searchValue.trim().length >= 4) {
            autoSearchTimer.current = setTimeout(() => {
                void runSearch(searchValue, 'serial');
            }, 400);
        }
        return () => {
            if (autoSearchTimer.current) clearTimeout(autoSearchTimer.current);
        };
    }, [searchValue, searchType, runSearch]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        if (autoSearchTimer.current) clearTimeout(autoSearchTimer.current);
        void runSearch(searchValue, searchType);
    };

    const toggleSelect = (pod: Fp1Device) => {
        setSelectedPodsMap((prev) => {
            if (prev[pod.uuid]) {
                const next = { ...prev };
                delete next[pod.uuid];
                return next;
            }
            return { ...prev, [pod.uuid]: pod };
        });
    };

    const toggleSelectAll = () => {
        const allResultsSelected = results.every((r) => !!selectedPodsMap[r.uuid]);
        if (allResultsSelected) {
            // Deselect all pods from the current result set
            setSelectedPodsMap((prev) => {
                const next = { ...prev };
                results.forEach((r) => delete next[r.uuid]);
                return next;
            });
        } else {
            // Add all current results to selection
            setSelectedPodsMap((prev) => {
                const next = { ...prev };
                results.forEach((r) => {
                    next[r.uuid] = r;
                });
                return next;
            });
        }
    };

    const searchLabel =
        searchType === 'serial'
            ? 'Pod serial number (partial OK, min 4 chars for auto-search)'
            : searchType === 'org'
            ? 'Organization name (partial)'
            : 'Athlete name or email';

    const selectedPods = Object.values(selectedPodsMap);

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssignmentReturnIcon color="primary" />
                    Initiate Return — Look Up Pod
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Search for the pod or customer to begin the return process. Select one or
                        more pods with the checkboxes, then click Process Return.
                    </Typography>
                    <Box
                        component="form"
                        onSubmit={handleSearch}
                        sx={{ display: 'flex', gap: 1.5, mb: 2, alignItems: 'flex-end' }}
                    >
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Search by</InputLabel>
                            <Select
                                value={searchType}
                                label="Search by"
                                onChange={(e) => {
                                    setSearchType(e.target.value as SearchType);
                                    setSearchValue('');
                                    setResults([]);
                                    setSearched(false);
                                    // Keep selectedPodsMap intact when switching search type
                                }}
                            >
                                <MenuItem value="serial">Serial Number</MenuItem>
                                <MenuItem value="org">Organization</MenuItem>
                                <MenuItem value="athlete">Athlete</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            label={searchLabel}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            sx={{ flex: 1 }}
                            placeholder="Enter search term…"
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={<SearchIcon />}
                            disabled={loading || !searchValue.trim()}
                        >
                            Search
                        </Button>
                    </Box>

                    {loading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                            <CircularProgress size={20} />
                            <Typography variant="body2">Searching…</Typography>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {searched && !loading && results.length === 0 && (
                        <Alert severity="info">No pods found matching your search.</Alert>
                    )}

                    {/* ── Pinned selected pods — persists across searches ── */}
                    {selectedPods.length > 0 && (
                        <Paper
                            variant="outlined"
                            sx={{ p: 1.5, mb: 2, borderRadius: 2, borderColor: 'primary.main' }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                }}
                            >
                                <Typography variant="subtitle2" color="primary">
                                    Selected for return ({selectedPods.length})
                                </Typography>
                                <Button
                                    size="small"
                                    color="error"
                                    onClick={() => setSelectedPodsMap({})}
                                >
                                    Clear all
                                </Button>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                {selectedPods.map((pod) => (
                                    <Chip
                                        key={pod.uuid}
                                        label={pod.serial_number}
                                        size="small"
                                        onDelete={() => toggleSelect(pod)}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontFamily: 'monospace', fontWeight: 600 }}
                                    />
                                ))}
                            </Box>
                        </Paper>
                    )}

                    {results.length > 0 && !loading && (
                        <Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 1,
                                }}
                            >
                                <Typography variant="subtitle2" color="text.secondary">
                                    {results.length} result{results.length !== 1 ? 's' : ''}
                                </Typography>
                                {results.length > 1 && (
                                    <Button size="small" onClick={toggleSelectAll}>
                                        {results.every((r) => !!selectedPodsMap[r.uuid])
                                            ? 'Deselect all'
                                            : 'Select all'}
                                    </Button>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {results.map((pod) => {
                                    const orgName =
                                        pod.assigned_org?.name ?? pod.organization_name ?? '—';
                                    const athleteName =
                                        pod.assigned_athlete?.name?.trim() ||
                                        pod.athlete_name ||
                                        null;
                                    const athleteEmail = pod.assigned_athlete?.email ?? null;
                                    const isSelected = !!selectedPodsMap[pod.uuid];

                                    return (
                                        <Paper
                                            key={pod.uuid}
                                            variant="outlined"
                                            onClick={() => toggleSelect(pod)}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                cursor: 'pointer',
                                                transition: 'border-color 0.15s, box-shadow 0.15s',
                                                borderColor: isSelected
                                                    ? 'primary.main'
                                                    : 'divider',
                                                boxShadow: isSelected
                                                    ? (t) => `0 0 0 1px ${t.palette.primary.main}`
                                                    : undefined,
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    boxShadow: (t) =>
                                                        `0 0 0 1px ${t.palette.primary.main}`,
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    flexWrap: 'wrap',
                                                    gap: 1,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        gap: 1,
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={isSelected}
                                                        size="small"
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => toggleSelect(pod)}
                                                        sx={{ p: 0, mt: 0.25 }}
                                                    />
                                                    <Box>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: 'monospace',
                                                                fontWeight: 700,
                                                                fontSize: '15px',
                                                            }}
                                                        >
                                                            {pod.serial_number}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mt: 0.25 }}
                                                        >
                                                            Type: {pod.device_type ?? '—'}{' '}
                                                            &nbsp;·&nbsp; Status:{' '}
                                                            {pod.status ?? '—'} &nbsp;·&nbsp;
                                                            Custody: {pod.custody_status ?? '—'}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {orgName}
                                                    </Typography>
                                                    {athleteName && (
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                        >
                                                            Athlete: {athleteName}
                                                            {athleteEmail
                                                                ? ` (${athleteEmail})`
                                                                : ''}
                                                        </Typography>
                                                    )}
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ mt: 0.25 }}
                                                    >
                                                        Sub status:{' '}
                                                        {pod.subscription_status ?? 'none'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {pod.subscription_status === 'active' && (
                                                <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                                                    Active subscription — cancel in Stripe during
                                                    the return workflow.
                                                </Alert>
                                            )}
                                        </Paper>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}

                    {selectedPods.length > 0 && (
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<AssignmentReturnIcon />}
                                onClick={() => setProcessReturnPods(selectedPods)}
                            >
                                Process Return ({selectedPods.length})
                            </Button>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {processReturnPods && (
                <ProcessReturnDialog
                    open={processReturnPods.length > 0}
                    pods={processReturnPods}
                    onClose={() => setProcessReturnPods(null)}
                    onReturned={() => {
                        const returnedUuids = new Set(processReturnPods.map((p) => p.uuid));
                        setResults((prev) => prev.filter((p) => !returnedUuids.has(p.uuid)));
                        setSelectedPodsMap((prev) => {
                            const next = { ...prev };
                            returnedUuids.forEach((u) => delete next[u]);
                            return next;
                        });
                        setProcessReturnPods(null);
                    }}
                />
            )}
        </>
    );
};

// ─── Athlete Change Requests Table ───────────────────────────────────────────

const AthleteRequestsTable: React.FC = () => {
    const [rows, setRows] = useState<AthleteChangeRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
    const [statusFilter, setStatusFilter] = useState<'requested' | 'completed' | 'rejected' | ''>(
        'requested',
    );
    const [editedNotes, setEditedNotes] = useState<Record<string, string>>({});
    const [processData, setProcessData] = useState<{
        requestUuid: string;
        requestNotes: string | null;
        pods: Fp1Device[];
    } | null>(null);
    const [fetchingPod, setFetchingPod] = useState<string | null>(null);
    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAthleteChangeRequests({
                status: statusFilter || undefined,
                page: paginationRef.current.page + 1,
                per_page: paginationRef.current.pageSize,
                sort: '-created_at',
            });
            setRows(res.data?.data ?? []);
            setTotalRows(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch athlete change requests', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        void fetchRows();
    }, [fetchRows, pagination.page, pagination.pageSize]);

    const handleSaveNotes = async (id: string) => {
        const notes = editedNotes[id];
        if (notes === undefined) return;
        const original = rows.find((r) => r.uuid === id);
        if (!original) return;
        try {
            await updateAthleteChangeRequest(id, {
                status: original.status,
                notes,
            });
            setEditedNotes((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            void fetchRows();
        } catch (err) {
            console.error('Failed to update notes', err);
        }
    };

    const handleCompleteReturn = async (row: AthleteChangeRequest) => {
        if (!row.pod_serial_number) return;
        setFetchingPod(row.uuid);
        try {
            const res = await getFp1Devices({
                serial_number: row.pod_serial_number,
                per_page: 5,
                page: 1,
            });
            const pods = res.data?.data ?? [];
            setProcessData({ requestUuid: row.uuid, requestNotes: row.notes ?? null, pods });
        } catch (err) {
            console.error('Failed to fetch pod for return', err);
        } finally {
            setFetchingPod(null);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'athlete_name',
            headerName: 'Athlete',
            width: 180,
            sortable: false,
            renderCell: (params) => {
                const u = params.row.user;
                return u ? `${u.first_name} ${u.last_name}` : '—';
            },
        },
        {
            field: 'contact',
            headerName: 'Contact',
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const u = params.row.user;
                if (!u) return <span>—</span>;
                const info = `Email: ${u.email}\nPhone: ${u.phone || 'N/A'}`;
                return (
                    <Tooltip
                        title={
                            <React.Fragment>
                                <span style={{ whiteSpace: 'pre-wrap' }}>{info}</span>
                                <IconButton
                                    onClick={() => copyToClipboard(info)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                            </React.Fragment>
                        }
                        arrow
                    >
                        <span style={{ cursor: 'pointer' }}>{u.email}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'change_request_type',
            headerName: 'Type',
            width: 130,
            sortable: false,
        },
        {
            field: 'pod_serial_number',
            headerName: 'Pod Serial',
            width: 140,
            sortable: false,
            renderCell: (params) => (
                <Typography sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
                    {params.row.pod_serial_number || '—'}
                </Typography>
            ),
        },
        {
            field: 'tracking_number',
            headerName: 'Return Tracking',
            width: 150,
            sortable: false,
            renderCell: (params) => params.row.tracking_number || '—',
        },
        {
            field: 'change_request_reason',
            headerName: 'Reason',
            width: 180,
            sortable: false,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                            {params.row.change_request_reason}
                        </span>
                    }
                    arrow
                >
                    <span
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'inline-block',
                            maxWidth: '100%',
                            cursor: 'pointer',
                        }}
                    >
                        {params.row.change_request_reason}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 80,
            sortable: false,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value}
                    color={
                        params.value === 'completed'
                            ? 'success'
                            : params.value === 'rejected'
                            ? 'error'
                            : 'default'
                    }
                />
            ),
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <TextField
                    size="small"
                    fullWidth
                    value={editedNotes[params.id as string] ?? (params.value as string) ?? ''}
                    onChange={(e) =>
                        setEditedNotes((prev) => ({
                            ...prev,
                            [params.id]: e.target.value,
                        }))
                    }
                    placeholder="Add notes…"
                    variant="standard"
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Submitted',
            width: 110,
            sortable: true,
            valueFormatter: (value: any) => formatTs(value),
        },
        {
            field: 'actions',
            headerName: '',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as AthleteChangeRequest;
                const isLoading = fetchingPod === row.uuid;
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {editedNotes[row.uuid] !== undefined && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSaveNotes(row.uuid)}
                            >
                                Save
                            </Button>
                        )}
                        {row.status !== 'completed' && row.pod_serial_number && (
                            <Button
                                size="small"
                                variant="contained"
                                color="error"
                                disabled={isLoading}
                                startIcon={
                                    isLoading ? (
                                        <CircularProgress size={12} color="inherit" />
                                    ) : undefined
                                }
                                onClick={() => handleCompleteReturn(row)}
                            >
                                Complete Return
                            </Button>
                        )}
                    </Box>
                );
            },
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status filter</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status filter"
                        onChange={(e) => {
                            setStatusFilter(e.target.value as typeof statusFilter);
                            setPagination((p) => ({ ...p, page: 0 }));
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="requested">Requested</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <DataGridPremium
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.uuid}
                    loading={loading}
                    rowCount={totalRows}
                    pagination
                    paginationMode="server"
                    paginationModel={pagination}
                    onPaginationModelChange={setPagination}
                    pageSizeOptions={[10, 25, 50]}
                    disableColumnMenu
                    checkboxSelection={false}
                    autoHeight
                    getRowHeight={() => 52}
                />
            </Paper>

            {processData && (
                <ProcessReturnDialog
                    open
                    pods={processData.pods}
                    changeRequestUuid={processData.requestUuid}
                    changeRequestType="athlete"
                    changeRequestNotes={processData.requestNotes}
                    onClose={() => setProcessData(null)}
                    onReturned={() => {
                        setProcessData(null);
                        void fetchRows();
                    }}
                />
            )}
        </Box>
    );
};

// ─── Org Subscription Change Requests Table ──────────────────────────────────

const OrgRequestsTable: React.FC = () => {
    const [rows, setRows] = useState<SubscriptionItemChangeRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
    const [statusFilter, setStatusFilter] = useState<'requested' | 'completed' | 'rejected' | ''>(
        'requested',
    );
    const [editedNotes, setEditedNotes] = useState<Record<string, string>>({});
    const [processData, setProcessData] = useState<{
        requestUuid: string;
        requestNotes: string | null;
        pods: Fp1Device[];
    } | null>(null);
    const [fetchingPod, setFetchingPod] = useState<string | null>(null);
    const paginationRef = useRef(pagination);
    paginationRef.current = pagination;

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getSubscriptionItemChangeRequests({
                status: statusFilter || undefined,
                page: paginationRef.current.page + 1,
                per_page: paginationRef.current.pageSize,
                sort: '-created_at',
            });
            setRows(res.data?.data ?? []);
            setTotalRows(res.data?.meta?.total ?? 0);
        } catch (err) {
            console.error('Failed to fetch org subscription change requests', err);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        void fetchRows();
    }, [fetchRows, pagination.page, pagination.pageSize]);

    const handleSaveNotes = async (id: string) => {
        const notes = editedNotes[id];
        if (notes === undefined) return;
        const original = rows.find((r) => r.uuid === id);
        if (!original) return;
        try {
            await updateSubscriptionItemChangeRequest(id, {
                status: original.status,
                notes,
            });
            setEditedNotes((prev) => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
            void fetchRows();
        } catch (err) {
            console.error('Failed to update notes', err);
        }
    };

    const handleCompleteReturn = async (row: SubscriptionItemChangeRequest) => {
        const orgUuid = row.organization?.uuid;
        if (!orgUuid) return;
        setFetchingPod(row.uuid);
        try {
            // Fetch deployed/return-requested pods for this org
            const [deployedRes, returnedRes] = await Promise.all([
                getFp1Devices({ organization_uuid: orgUuid, status: 'deployed', per_page: 50 }),
                getFp1Devices({ organization_uuid: orgUuid, status: 'returned', per_page: 50 }),
            ]);
            const pods = [...(deployedRes.data?.data ?? []), ...(returnedRes.data?.data ?? [])];
            setProcessData({ requestUuid: row.uuid, requestNotes: row.notes ?? null, pods });
        } catch (err) {
            console.error('Failed to fetch pods for return', err);
        } finally {
            setFetchingPod(null);
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'org_name',
            headerName: 'Organization',
            width: 200,
            sortable: false,
            renderCell: (params) => params.row.organization?.name ?? '—',
        },
        {
            field: 'contact',
            headerName: 'Contact',
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const c = params.row.organization?.creator;
                if (!c) return <span>—</span>;
                const info = `Email: ${c.email}\nPhone: ${c.phone || 'N/A'}`;
                return (
                    <Tooltip
                        title={
                            <React.Fragment>
                                <span style={{ whiteSpace: 'pre-wrap' }}>{info}</span>
                                <IconButton
                                    onClick={() => copyToClipboard(info)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    <ContentCopyIcon fontSize="inherit" />
                                </IconButton>
                            </React.Fragment>
                        }
                        arrow
                    >
                        <span style={{ cursor: 'pointer' }}>{c.email}</span>
                    </Tooltip>
                );
            },
        },
        {
            field: 'change_request_type',
            headerName: 'Type',
            width: 130,
            sortable: false,
        },
        {
            field: 'change_request_reason',
            headerName: 'Reason',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <Tooltip
                    title={
                        <span style={{ whiteSpace: 'pre-wrap' }}>
                            {params.row.change_request_reason}
                        </span>
                    }
                    arrow
                >
                    <span
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'inline-block',
                            maxWidth: '100%',
                            cursor: 'pointer',
                        }}
                    >
                        {params.row.change_request_reason}
                    </span>
                </Tooltip>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 80,
            sortable: false,
            renderCell: (params) => (
                <Chip
                    size="small"
                    label={params.value}
                    color={
                        params.value === 'completed'
                            ? 'success'
                            : params.value === 'rejected'
                            ? 'error'
                            : 'default'
                    }
                />
            ),
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 200,
            sortable: false,
            renderCell: (params) => (
                <TextField
                    size="small"
                    fullWidth
                    value={editedNotes[params.id as string] ?? (params.value as string) ?? ''}
                    onChange={(e) =>
                        setEditedNotes((prev) => ({
                            ...prev,
                            [params.id]: e.target.value,
                        }))
                    }
                    placeholder="Add notes…"
                    variant="standard"
                />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Submitted',
            width: 110,
            sortable: true,
            valueFormatter: (value: any) => formatTs(value),
        },
        {
            field: 'actions',
            headerName: '',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const row = params.row as SubscriptionItemChangeRequest;
                const isLoading = fetchingPod === row.uuid;
                return (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {editedNotes[row.uuid] !== undefined && (
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleSaveNotes(row.uuid)}
                            >
                                Save
                            </Button>
                        )}
                        {row.status !== 'completed' && row.organization?.uuid && (
                            <Button
                                size="small"
                                variant="contained"
                                color="error"
                                disabled={isLoading}
                                startIcon={
                                    isLoading ? (
                                        <CircularProgress size={12} color="inherit" />
                                    ) : undefined
                                }
                                onClick={() => handleCompleteReturn(row)}
                            >
                                Complete Return
                            </Button>
                        )}
                    </Box>
                );
            },
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status filter</InputLabel>
                    <Select
                        value={statusFilter}
                        label="Status filter"
                        onChange={(e) => {
                            setStatusFilter(e.target.value as typeof statusFilter);
                            setPagination((p) => ({ ...p, page: 0 }));
                        }}
                    >
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="requested">Requested</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="rejected">Rejected</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <DataGridPremium
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.uuid}
                    loading={loading}
                    rowCount={totalRows}
                    pagination
                    paginationMode="server"
                    paginationModel={pagination}
                    onPaginationModelChange={setPagination}
                    pageSizeOptions={[10, 25, 50]}
                    disableColumnMenu
                    checkboxSelection={false}
                    autoHeight
                    getRowHeight={() => 52}
                />
            </Paper>

            {processData && (
                <ProcessReturnDialog
                    open
                    pods={processData.pods}
                    changeRequestUuid={processData.requestUuid}
                    changeRequestType="org"
                    changeRequestNotes={processData.requestNotes}
                    onClose={() => setProcessData(null)}
                    onReturned={() => {
                        setProcessData(null);
                        void fetchRows();
                    }}
                />
            )}
        </Box>
    );
};

// ─── Replacement Returns Section ─────────────────────────────────────────────

const ReplacementReturnsSection: React.FC = () => {
    const [rows, setRows] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);
    const [podSerialMap, setPodSerialMap] = useState<Record<string, string | null>>({});
    const [trackingStatusMap, setTrackingStatusMap] = useState<
        Record<string, string | null | undefined>
    >({});
    const fetchedRef = useRef(false);
    const [returnDialogOrder, setReturnDialogOrder] = useState<Order | null>(null);

    const loadPodSerials = useCallback(async (orders: Order[]) => {
        const results = await Promise.allSettled(orders.map((o) => getOrderPodAssignments(o.uuid)));
        const newMap: Record<string, string | null> = {};
        results.forEach((result, i) => {
            const orderUuid = orders[i].uuid;
            if (result.status === 'fulfilled') {
                // The pod actually being returned is the OLD pod, not the new one shipped out.
                newMap[orderUuid] = result.value.data?.data?.old_pod?.serial_number ?? null;
            } else {
                newMap[orderUuid] = null;
            }
        });
        setPodSerialMap((prev) => ({ ...prev, ...newMap }));
    }, []);

    const loadTrackingStatuses = useCallback(async (orders: Order[]) => {
        // Only fetch Shippo status for orders that have a return tracking number
        const ordersWithTracking = orders.filter((o) => !!o.return_tracking_number);
        // Mark orders without tracking as undefined so they show "—" immediately
        const noTrackingMap: Record<string, string | null> = {};
        orders
            .filter((o) => !o.return_tracking_number)
            .forEach((o) => {
                noTrackingMap[o.uuid] = null;
            });
        setTrackingStatusMap((prev) => ({ ...prev, ...noTrackingMap }));

        if (ordersWithTracking.length === 0) return;

        const results = await Promise.allSettled(
            ordersWithTracking.map((o) => getOrderReturnTrackingEvidence(o.uuid)),
        );
        const newMap: Record<string, string | null> = {};
        results.forEach((result, i) => {
            const orderUuid = ordersWithTracking[i].uuid;
            if (result.status === 'fulfilled') {
                newMap[orderUuid] = result.value.data?.data?.tracking?.current_status ?? null;
            } else {
                // null = API not available, fall back to carrier link
                newMap[orderUuid] = null;
            }
        });
        setTrackingStatusMap((prev) => ({ ...prev, ...newMap }));
    }, []);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        setPodSerialMap({});
        setTrackingStatusMap({});
        try {
            const res = await getOrders({
                order_type: 'replacement',
                per_page: 100,
                sort: '-created_at',
            });
            // Show replacement orders that have both tracking numbers and haven't been archived
            // (archived = return has been processed via the Complete Return button).
            const withBothTracking = (res.data?.data ?? []).filter(
                (o) =>
                    !!o.return_tracking_number &&
                    !!o.shipping_tracking_number &&
                    o.order_status !== 'archived',
            );
            setRows(withBothTracking);
            if (withBothTracking.length > 0) {
                void loadPodSerials(withBothTracking);
                void loadTrackingStatuses(withBothTracking);
            }
        } catch (err) {
            console.error('Failed to fetch replacement orders', err);
        } finally {
            setLoading(false);
        }
    }, [loadPodSerials, loadTrackingStatuses]);

    useEffect(() => {
        if (!fetchedRef.current) {
            fetchedRef.current = true;
            void fetchOrders();
        }
    }, [fetchOrders]);

    const columns: GridColDef[] = [
        {
            field: 'recipient',
            headerName: 'Recipient',
            width: 200,
            sortable: false,
            renderCell: (params) => {
                const o = params.row as Order;
                return (
                    o.buyer_org_name ??
                    (o.buyer_athlete
                        ? `${o.buyer_athlete.first_name} ${o.buyer_athlete.last_name}`
                        : '—')
                );
            },
        },
        {
            field: 'pod_serial',
            headerName: 'Pod Serial',
            width: 160,
            sortable: false,
            renderCell: (params) => {
                const serial = podSerialMap[params.row.uuid];
                if (serial === undefined) return <CircularProgress size={14} />;
                if (!serial) return <span>—</span>;
                return (
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '13px' }}>
                        {serial}
                    </Typography>
                );
            },
        },
        {
            field: 'return_tracking_number',
            headerName: 'Return Tracking',
            width: 190,
            sortable: false,
            renderCell: (params) => {
                const tracking = params.value as string | null | undefined;
                if (!tracking) return <span>—</span>;
                return (
                    <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNewIcon fontSize="inherit" />}
                        onClick={() =>
                            window.open(
                                `https://www.ups.com/track?loc=en_US&tracknum=${tracking}`,
                                '_blank',
                                'noopener,noreferrer',
                            )
                        }
                        sx={{ fontSize: '12px', fontFamily: 'monospace', px: 0, minWidth: 0 }}
                    >
                        {tracking}
                    </Button>
                );
            },
        },
        {
            field: 'order_type',
            headerName: 'Type',
            width: 120,
            renderCell: () => (
                <Chip size="small" label="Replacement" variant="outlined" color="primary" />
            ),
        },
        {
            field: 'created_at',
            headerName: 'Order Date',
            width: 130,
            valueFormatter: (value: any) => formatTs(value),
        },
        {
            field: 'tracking_status',
            headerName: 'Return Status',
            width: 190,
            sortable: false,
            renderCell: (params) => {
                const orderUuid = params.row.uuid as string;
                const trackingNumber = params.row.return_tracking_number as string | null;
                const status = trackingStatusMap[orderUuid];

                // Still loading
                if (status === undefined) {
                    return <CircularProgress size={14} />;
                }

                // API unavailable — fall back to carrier link
                if (status === null) {
                    if (!trackingNumber) return <span>—</span>;
                    return (
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon fontSize="inherit" />}
                            onClick={() =>
                                window.open(
                                    `https://www.ups.com/track?loc=en_US&tracknum=${trackingNumber}`,
                                    '_blank',
                                    'noopener,noreferrer',
                                )
                            }
                            sx={{ fontSize: '11px' }}
                        >
                            Check Carrier
                        </Button>
                    );
                }

                const lower = status.toLowerCase();
                const delivered =
                    lower.includes('delivered') || lower.includes('delivery_attempted');
                const inTransit =
                    lower.includes('transit') ||
                    lower.includes('in_transit') ||
                    lower.includes('out_for_delivery') ||
                    lower.includes('pre_transit');

                return (
                    <Chip
                        size="small"
                        label={status.replace(/_/g, ' ')}
                        color={delivered ? 'success' : inTransit ? 'info' : 'default'}
                        sx={{ fontSize: '11px', textTransform: 'capitalize' }}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: '',
            width: 160,
            sortable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    variant="contained"
                    color="error"
                    startIcon={<AssignmentReturnIcon fontSize="inherit" />}
                    onClick={() => setReturnDialogOrder(params.row as Order)}
                    sx={{ fontSize: '12px' }}
                >
                    Complete Return
                </Button>
            ),
        },
    ];

    if (!loading && rows.length === 0) return null;

    return (
        <Box sx={{ mb: 3 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    mb: 1.5,
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <LocalShippingIcon color="primary" fontSize="small" />
                        <Typography variant="h6" fontWeight={600}>
                            Replacement Pod Returns
                        </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        Replacement orders with both an outbound and inbound tracking number —
                        monitor whether the defective pod is on its way back.
                    </Typography>
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                        fetchedRef.current = false;
                        void fetchOrders();
                    }}
                >
                    Refresh
                </Button>
            </Box>
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <DataGridPremium
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.uuid}
                    loading={loading}
                    disableColumnMenu
                    autoHeight
                    hideFooter={rows.length <= 10}
                    getRowHeight={() => 52}
                />
            </Paper>

            <ReplacementReturnDialog
                open={!!returnDialogOrder}
                order={returnDialogOrder}
                prefilledSerial={
                    returnDialogOrder ? podSerialMap[returnDialogOrder.uuid] ?? null : null
                }
                onClose={() => setReturnDialogOrder(null)}
                onCompleted={() => {
                    // Remove the processed order from the table immediately
                    if (returnDialogOrder) {
                        const uuid = returnDialogOrder.uuid;
                        setRows((prev) => prev.filter((r) => r.uuid !== uuid));
                    }
                    setReturnDialogOrder(null);
                }}
            />
        </Box>
    );
};

// ─── Main Tab Component ──────────────────────────────────────────────────────

const Fp1ReturnsTab: React.FC = () => {
    const theme = useTheme();
    const [initiateOpen, setInitiateOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [requestsTab, setRequestsTab] = useState(0);

    return (
        <Box>
            {/* Top section — Initiate Return */}
            <Paper
                variant="outlined"
                sx={{
                    p: 2.5,
                    mb: 3,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Initiate a Return
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        Look up a pod by serial number, organization, or athlete to begin the return
                        process and review subscription status.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="large"
                        startIcon={<HistoryIcon />}
                        onClick={() => setHistoryOpen(true)}
                    >
                        Return History
                    </Button>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<AssignmentReturnIcon />}
                        onClick={() => setInitiateOpen(true)}
                    >
                        Initiate Return
                    </Button>
                </Box>
            </Paper>

            <InitiateReturnDialog open={initiateOpen} onClose={() => setInitiateOpen(false)} />
            <ReturnHistoryDialog open={historyOpen} onClose={() => setHistoryOpen(false)} />

            <Divider sx={{ mb: 3 }} />

            <ReplacementReturnsSection />

            {/* Bottom section — Pending return / cancellation requests */}
            <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
                    Return &amp; Cancellation Requests
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Athletes and organizations who have submitted a return or subscription
                    cancellation request. Click &ldquo;Complete Return&rdquo; to process the pod
                    return and mark the request as completed.
                </Typography>

                <Tabs
                    value={requestsTab}
                    onChange={(_, v) => setRequestsTab(v)}
                    sx={{ mb: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
                >
                    <Tab label="Athlete Requests" />
                    <Tab label="Organization Requests" />
                </Tabs>

                {requestsTab === 0 && <AthleteRequestsTable />}
                {requestsTab === 1 && <OrgRequestsTable />}
            </Box>
        </Box>
    );
};

export default Fp1ReturnsTab;
